import { defineStore } from 'pinia';
import { store } from '@common/store/store';
import { library } from "@common/library/library";

import { alertComponent } from './alert';
import { contextMenuCmp } from './contextMenu';
import { buttonComponent } from './button';
import { splitButtonComponent } from './splitbutton';
import { checkboxComponent } from './checkbox';
import { dateComponent } from './date';
import { timeComponent } from './time';
import { datamapComponent } from './datamapControl.vue';
import { dropdownComponent } from './dropdown';
import { dropdownButtonComponent } from './dropdownbutton';
import { dropdownTreeComponent } from './dropdowntree';
import { multiSelectComponent } from './multiselect';
import { imageComponent } from './image';
import { numberComponent } from './number';
import { textComponent } from './text';
import { iconPickerComponent } from './iconPicker';
import { listComponent } from './list';
import { colorComponent } from './color';
import { urlComponent } from './url';
import { htmlComponent } from './html';
import { arrayOfObjectsComponent } from './arrayOfObjects';
import { objectComponent } from './object';
import { checkboxListComponent } from "./checkboxList";
import { radioButtonComponent } from "./radioButtonList";
import { actionFieldComponent } from "./actionField";
import { calcControlComponent } from "./calcControl";
import { progressComponent } from "./progress";
import { uploadFileComponent } from "./uploadFile";
import { logicControl } from "./logicControl";
import { pdfControl } from "./pdf";
import { listViewCmp } from "./listView";
import { toastComponent } from "./toast";
import dataSourceComponent from "./dataSource.vue";
import { labelComponent } from "./label.vue";
import { youtubeComponent } from "./youtube.vue";
import { gridComponent } from "./grid.vue";
import { hiddenComponent } from "./hidden.vue";

// available component types
const controlTypes = {
    // @todo alert, warning, error, info to the one component
    alert: alertComponent,
    warning: alertComponent,
    error: alertComponent,
    info: alertComponent,
    success: alertComponent,
    button: buttonComponent,
    contextmenu: contextMenuCmp,
    splitbutton: splitButtonComponent,
    dropdownbutton: dropdownButtonComponent,
    dropdowntree: dropdownTreeComponent,
    checkbox: checkboxComponent,
    boolean: checkboxComponent,
    // @todo data, time to the one component
    date: dateComponent,
    time: timeComponent,
    datamapeditor: datamapComponent,
    datasource: dataSourceComponent,
    dropdown: dropdownComponent,
    multiselect: multiSelectComponent,
    image: imageComponent,
    number: numberComponent,
    string: textComponent,
    text: textComponent,
    iconpicker: iconPickerComponent,
    // @todo move pass to the string
    password: textComponent,
    list: listComponent,
    color: colorComponent,
    url: urlComponent,
    html: htmlComponent,
    array: arrayOfObjectsComponent,
    jsonarray: arrayOfObjectsComponent,
    checkbox_list: checkboxListComponent,
    radio_button_list: radioButtonComponent,
    radio: radioButtonComponent,
    object: objectComponent,
    actionfield: actionFieldComponent,
    calccontrol: calcControlComponent,
    progress: progressComponent,
    upload: uploadFileComponent,
    logiceditor: logicControl,
    logic: logicControl,
    pdf: pdfControl,
    listview: listViewCmp,
    toast: toastComponent,
    label: labelComponent,
    youtube: youtubeComponent,
    grid: gridComponent,
    hidden: hiddenComponent
};

let controlTypesItems = [];
for (let i in controlTypes) {
    if (i === 'length') continue;
    controlTypesItems.push({
        text: i,
        value: i
    });
    controlTypesItems.sort((a, b) => a.text.localeCompare(b.text));
}

let controlGlobalEditorSettings = {
    configSource: {
        label: 'Control Config Source',
        type: 'dropdowntree',
        fields: {
            text: 'text',
            value: 'value',
            hasChildren: 'hasChild',
            parentValue: 'pId',
        },
        allowFiltering: true,
        filterType: 'Contains',
        itemsSource: 'configStore.allFieldOptions()',
        message: 'Using this requires the name of this control to be an exact match of a system field.  eg: VOL_FIRST_NAME, or UDFs like CA#Vol Group Questions/Some '
    },
    prioritizeLocalSourceConfig: {
        label: 'Choose your preferred configuration source.',
        type: 'dropdown',
        helpText: 'Choose whether to prioritize server-side configurations or local settings when merging control\'s data object.',
        items: [
            {
                label: 'Local Source',
                value: true
            },
            {
                label: 'Server Source (default)',
                value: false
            }
        ],
        default: false
    },
    name: {
        type: 'string',
        label: 'Name'
    },
    type: {
        type: "dropdown",
        label: "Control Type",
        fields: { text: 'text', value: 'value' },
        items: library.cloneObj(controlTypesItems)
    },
    label: {
        type: 'string',
        label: 'Label',
        helpText: 'You can also use basic values from the record with interpolation eg: {{record.OPP_TITLE}}'
    },
    minDate: {
        label: 'Min Date',
        type: 'date',
        name: 'minDate',
        helpText: '',
        saveAsObject: true,
        allowRelativeDate: true,
        logic: [
            {
                record: 'self',
                prop: 'display',
                valueType: 'criteria',
                criteria: 'this.valuesMap.controls[__index__].type === "date"'
            }
        ]
    },
    maxDate: {
        label: 'Max Date',
        type: 'date',
        name: 'maxDate',
        saveAsObject: true,
        allowRelativeDate: true,
        helpText: '',
        logic: [
            {
                record: 'self',
                prop: 'display',
                valueType: 'criteria',
                criteria: 'this.valuesMap.controls[__index__].type === "date"'
            }
        ]
    },
    cssClass: {
        type: 'string',
        label: 'CSS Classes',
        helpText: 'Add CSS classes'
    },
    storePropValueSync: {
        type: 'string',
        label: 'Sync With Store Prop Value',
    },
    default: {
        label: 'Default',
        type: 'string',
        id: 'default',
        helpText: '',
        logic: [
            {
                record: 'self',
                prop: 'type',
                valueType: 'value',
                value: 'date',
                criteria: 'this.valuesMap.controls[__index__].type === "date"'
            },
            {
                record: 'self',
                prop: 'type',
                valueType: 'value',
                value: 'number',
                criteria: 'this.valuesMap.controls[__index__].type === "number"'
            },
            {
                record: 'self',
                prop: 'type',
                valueType: 'value',
                value: 'string',
                criteria: 'this.valuesMap.controls[__index__].type === "string"'
            },
            {
                record: 'self',
                prop: 'type',
                valueType: 'path',
                valuePath: 'valuesMap.controls[__index__].defaultValueType',
                criteria: 'this.valuesMap.controls[__index__].type === "dropdown"'
            }
        ]
    },
    defaultValueType: {
        label: 'Default Value Type',
        type: 'dropdown',
        id: 'defaultValueType',
        helpText: '',
        items: [
            {
                label: 'String',
                value: 'string'
            },
            {
                label: 'Number',
                value: 'number'
            }
        ]
    },
    helpText: {
        type: 'text',
        label: 'Help Text'
    },
    message: {
        type: 'text',
        label: 'Message'
    },
    hideLabel: {
        type: 'boolean',
        id: 'hideLabel',
        label: 'Hide Label',
        default: false,
        helpText: 'Hide the label of this control.'
    },
    placeholder: {
        type: 'string',
        label: 'Placeholder'
    },
    mask: {
        label: 'Field Mask / Field Verification Pattern',
        type: 'dropdown',
        id: 'textMaskConfig',
        helpText: 'Choose a mask/field pattern from the dropdown or if you select Custom, see https://ej2.syncfusion.com/vue/documentation/maskedtextbox/mask-configuration for documentation',
        value: 'value',
        items: [
            { text: 'Phone Number', value: '(000)\\ 000\\-0000' },
            { text: 'SSN', value: '000\\-00\\-0000' },
            { text: 'State', value: 'LL' },
            { text: 'Zip Code', value: '00000' },
            { text: 'Custom', value: 'custom' },
        ],
        fields: {
            text: 'text',
            value: 'value',
            type: 'type'
        },
        logic: [
            {
                record: 'self',
                prop: 'display',
                valueType: 'criteria',
                criteria: 'this.valuesMap.controls[__index__].type === "string"'
            },
            {
                record: 'self',
                prop: 'value',
                valueType: 'value',
                value: '',
                criteria: 'this.valuesMap.controls[__index__].mask === "custom"'
            }
        ]
    },
    customMask: {
        label: 'Custom Mask',
        type: 'string',
        id: 'customMask',
        helpText: 'Refer to https://ej2.syncfusion.com/vue/documentation/maskedtextbox/mask-configuration for documentation on how to create your own masked text field.',
        showClearButton: true,
        display: false,
        logic: [
            {
                record: 'self',
                prop: 'display',
                valueType: 'criteria',
                criteria: 'this.valuesMap.controls[__index__].mask === "custom"'
            },
            {
                record: 'self',
                prop: 'value',
                valueType: 'value',
                value: '',
                criteria: 'this.valuesMap.controls[__index__].mask !== "custom"'
            }
        ]
    },
    asField: {
        type: "boolean",
        label: "Show value only",
        default: false
    },
    width: {
        type: 'string',
        label: 'Control Width',
        id: 'width'
    },
    disabled: {
        type: "boolean",
        label: "Disabled",
        default: false
    },
    required: {
        type: "boolean",
        label: "Required",
        default: false
    },
    logic: {
        label: 'Control logic',
        id: 'logic',
        type: 'logicEditor',
        default: [],
        helpText: 'A list of logic rules for control properties.',
        otherProps: {
            recordTypes: [
                { text: 'Record', value: 'object2' }
            ],
        }
    },
};
let textControlEditorSettings = {
    showExpandMinimizeBtns: {
        label: 'Show Expand/Minimize Buttons',
        type: 'boolean',
        id: 'showExpandMinimizeBtns',
        default: false,
        helpText: 'Show Expand/Minimize Buttons'
    }
};
const optionControlEditorSettings = {
    onchange: {
        type: 'actionfield',
        label: 'Change Action',
        itemsSource: 'actionStore.getActionOptions()',
        fields: {
            text: 'text',
            value: 'value'
        },
        default: {
            action: '',
            params: {}
        }
    },
    itemsType: {
        label: 'Options Source Type',
        type: 'dropdown',
        items: [
            { text: 'From Source', value: 'source' },
            { text: 'User Created', value: 'local' },
        ],
        fields: {
            text: 'text',
            value: 'value'
        },
        helpText: 'What type of options source?',
        logic: [
            {
                record: 'self',
                prop: 'display',
                valueType: 'criteria',
                criteria: `this.valuesMap.controls[__index__]
                    && (
                        this.valuesMap.controls[__index__].type === "dropdown"
                        || this.valuesMap.controls[__index__].type === "dropdowntree"
                        || this.valuesMap.controls[__index__].type === "dropdownbutton"
                        || this.valuesMap.controls[__index__].type === "radio"
                        || this.valuesMap.controls[__index__].type === "radio_button_list"
                    )`
            }
        ]
    },
    itemsSource: {
        label: 'Options Source',
        type: 'datasource',
        mode: 'Box',
        itemsSource: 'configStore.getAllSourceList()',
        allowFiltering: true,
        // type: 'dropdowntree',
        id: 'itemsSource',
        // itemsSource: 'configStore.itemsSourceOptions()',
        fields: {
            value: 'value',
            text: 'text',
            parentValue: 'pId',
            hasChildren: 'hasChild'
        },
        helpText: 'If the control is a dropdown or dropdown tree, provide a source for options.',
        logic: [
            {
                record: 'self',
                prop: 'display',
                valueType: 'criteria',
                criteria: 'this.valuesMap.controls[__index__].itemsType === "source"'
            }
        ],
        default: []
    },
    items: {
        label: 'Options',
        id: 'items',
        type: 'ARRAY',
        default: [],
        itemConfig: {
            itemType: 'object',
            primaryKey: 'text',
            permissions: {
                canMove: true,
                canDelete: true,
                allowAddItem: true
            },
            keys: {
                text: {
                    type: 'string',
                    id: 'text',
                    label: 'Label'
                },
                value: {
                    type: 'string',
                    id: 'value',
                    label: 'Value'
                }
            },
            newItem: {
                text: '',
                value: ''
            }
        },
        logic: [
            {
                record: 'self',
                prop: 'display',
                valueType: 'criteria',
                criteria: 'this.valuesMap.controls[__index__].itemsType === "local"'
            }
        ]
    },
    fields: {
        type: 'OBJECT',
        label: 'Option Fields',
        id: 'fields',
        properties: {
            text: {
                type: 'string',
                id: 'text',
                label: 'Option Text Source',
                default: 'label'
            },
            value: {
                type: 'string',
                id: 'value',
                label: 'Option Value Source',
                default: 'value'
            },
            hasChildren: {
                type: 'string',
                id: 'hasChildren',
                label: 'Option Has Children Property'
            },
            parentValue: {
                type: 'string',
                id: 'parentValue',
                label: 'Option Parent Index'
            }
        },
        default: {
            text: 'text',
            value: 'value'
        }
    },
    itemsFilter: {
        type: 'string',
        label: 'Items Filter Logic',
        id: 'itemsFilter',
        helpText: 'Apply a logic filter to the list of items'
    },
    itemsSort: {
        type: 'OBJECT',
        label: 'Options Sort',
        id: 'itemsSort',
        default: {
            sortBy: [],
            direction: 'asc'
        },
        properties: {
            sortBy: {
                type: 'multiselect',
                id: 'sortBy',
                mode: 'Box',
                itemsSource: 'this.sortByOptions',
                itemsFilter: 'this.component.controls.controls.value[__index__].itemsSource.indexOf(this.item.func) !== -1',
                fields: { text: 'text', value: 'value' },
                label: "Sort By",
                allowFiltering: true,
                allowCustomValue: true,
                default: []
            },
            direction: {
                type: 'dropdown',
                label: 'Direction',
                default: 'asc',
                items: [
                    'asc',
                    'desc'
                ],
                id: 'direction'
            }
        }
    },
    showCheckBox: {
        label: 'Show Checkboxes',
        type: 'boolean',
        id: 'showCheckBox',
        helpText: 'Show options with checkboxes'
    },
    allowMultiSelection: {
        label: 'Allow Multi-select',
        type: 'boolean',
        id: 'allowMultiSelection',
        helpText: 'Allow the dropdown to be multiselect'
    }
};
const dropdownControlEditorSettings = {
    itemTemplate: {
        type: 'text',
        label: 'Item Template',
        id: 'itemTemplate',
        helpText: 'Enter a custom template for your dropdown options. use {{data.optionProp}} or {{config.configProp}} to interpolate values'
    },
    allowFiltering: {
        label: 'Enable Filtering',
        type: 'boolean',
        id: 'allowFiltering',
        helpText: 'Show a search bar for filtering with the dropdown.'
    },
    filterProps: {
        label: 'Props to use for search matches',
        type: 'multiselect',
        allowCustomValue: true,
        items: ['any'],
        id: 'filterProps',
        helpText: 'Type in the props from your options that you want to filter by, or choose "all" to search all properties (note: this may cause slowness)'
    }
};
const arrayControlEditorSettings = {
    itemConfigObj: { // This gets mapped to itemConfig for the new array control
        label: 'Item Config',
        id: 'itemConfigObj',
        type: 'object',
        properties: {
            primaryKey: {
                label: 'Primary Key',
                id: 'primaryKey',
                type: 'string',
                helpText: 'The primary key to use for labeling each item in the array.'
            },
            itemType: {
                label: 'Item Type',
                id: 'itemType',
                type: 'dropdown',
                items: [
                    { text: 'Object', value: 'object' },
                    { text: 'Array', value: 'array' },
                ],
                fields: {
                    text: 'text',
                    value: 'value'
                }
            },
            permissions: {
                type: 'object',
                label: 'Permissions',
                id: 'permissions',
                properties: {
                    canMove: {
                        label: 'Can Move',
                        type: 'boolean',
                        id: 'canMove',
                        helpText: 'Allow users to move the items in this list.'
                    },
                    canDelete: {
                        label: 'Can Delete',
                        type: 'boolean',
                        id: 'canDelete',
                        helpText: 'Allow users to delete the items in this list.'
                    },
                    allowAddItem: {
                        label: 'Can Add New Items',
                        type: 'boolean',
                        id: 'allowAddItem',
                        helpText: 'Allow users to add new items to this list.'
                    },
                }
            },
            itemKeys: {
                label: 'Properties of the items in the array.',
                type: 'array',
                itemConfig: {
                    primaryKey: 'itemProperty',
                    itemType: 'object',
                    permissions: {
                        canMove: true,
                        canDelete: true,
                        allowAddItem: true
                    },
                    keys: {
                        itemProperty: {
                            label: 'Property',
                            type: 'string',
                            id: 'itemProperty',
                            helpText: 'Arrays of objects have properties for each object.  Define them here.'
                        },
                        type: {
                            label: 'Item Property Data Type',
                            type: 'dropdown',
                            items: library.cloneObj(controlTypesItems),
                            fields: { text: 'text', value: 'value' },
                            helpText: 'What type of data will be in this property?',
                            id: 'type',
                        },
                        enableHtml: {
                            label: 'Enable Rich Text Editor',
                            type: 'boolean',
                            helpText: 'Enable Rich Text Editor',
                            id: 'enableHtml'
                        },
                        itemsType: {
                            label: 'Options Source Type',
                            type: 'dropdown',
                            items: [
                                { text: 'From Source', value: 'source' },
                                { text: 'User Created', value: 'local' },
                            ],
                            fields: {
                                text: 'text',
                                value: 'value'
                            },
                            helpText: 'What type of options source?',
                            logic: [
                                {
                                    record: 'self',
                                    prop: 'display',
                                    valueType: 'criteria',
                                    criteria: `this.valuesMap.controls[__index__].itemConfigObj.itemKeys[__index__]
                                        && (
                                            this.valuesMap.controls[__index__].itemConfigObj.itemKeys[__index__].type === "dropdown"
                                            || this.valuesMap.controls[__index__].itemConfigObj.itemKeys[__index__].type === "dropdowntree"
                                       )`
                                }
                            ]
                        },
                        itemsSource: {
                            label: 'Options Source',
                            type: 'dropdowntree',
                            id: 'itemsSource',
                            itemsSource: 'configStore.itemsSourceOptions()',
                            fields: {
                                value: 'value',
                                text: 'text',
                                parentValue: 'pId',
                                hasChildren: 'hasChild'
                            },
                            helpText: 'If the control is a dropdown or dropdown tree, provide a source for options.',
                            logic: [
                                {
                                    record: 'self',
                                    prop: 'display',
                                    valueType: 'criteria',
                                    criteria: 'this.valuesMap.controls[__index__].itemConfigObj.itemKeys[__index__].itemsType === "source"'
                                }
                            ]
                        },
                        items: {
                            label: 'Options',
                            id: 'items',
                            type: 'array',
                            default: [],
                            itemConfig: {
                                itemType: 'object',
                                primaryKey: 'text',
                                permissions: {
                                    canMove: true,
                                    canDelete: true,
                                    allowAddItem: true
                                },
                                keys: {
                                    text: {
                                        type: 'string',
                                        id: 'text',
                                        label: 'Label'
                                    },
                                    value: {
                                        type: 'string',
                                        id: 'value',
                                        label: 'Value'
                                    }
                                },
                                newItem: {
                                    text: '',
                                    value: ''
                                }
                            },
                            logic: [
                                {
                                    record: 'self',
                                    prop: 'display',
                                    valueType: 'criteria',
                                    criteria: 'this.valuesMap.controls[__index__].itemConfigObj.itemKeys[__index__].itemsType === "local"'
                                }
                            ]
                        },
                        fields: {
                            label: 'Option Fields',
                            type: 'object',
                            properties: {
                                text: {
                                    type: 'string',
                                    label: 'Text Property',
                                    id: 'text',
                                    helpText: 'property used for the display text.'
                                },
                                value: {
                                    type: 'string',
                                    label: 'Value Property',
                                    id: 'value',
                                    helpText: 'property used for the option\'s value.'
                                },
                                parentValue: {
                                    type: 'string',
                                    label: 'parentValue Property',
                                    id: 'parentValue',
                                    helpText: 'Property used to store the parent\'s ID.',
                                    message: 'Use this setting for options that have nested values.'
                                },
                                hasChildren: {
                                    type: 'string',
                                    label: 'hasChildren Property',
                                    id: 'hasChildren',
                                    helpText: 'property used to indicate that the option has children.',
                                    message: 'Use this setting for options that have nested values.'
                                }
                            },
                            id: 'fields',
                            helpText: 'Set the fields object for the item options.',
                            logic: [
                                {
                                    record: 'self',
                                    prop: 'display',
                                    valueType: 'criteria',
                                    criteria: `this.valuesMap.controls[__index__].itemConfigObj.itemKeys[__index__]
                                        && (
                                            this.valuesMap.controls[__index__].itemConfigObj.itemKeys[__index__].type === "dropdown"
                                            || this.valuesMap.controls[__index__].itemConfigObj.itemKeys[__index__].type === "dropdowntree"
                                        )`
                                }
                            ],
                            default: { text: 'text', value: 'value' }
                        },
                        label: {
                            label: 'Property Label (for the editor)',
                            type: 'string',
                            id: 'label',
                            helpText: ''
                        },
                        default: {
                            label: 'Default Value',
                            type: 'string',
                            id: 'default',
                            helpText: 'Default Value for the property when a new item is added.'
                        },
                        helpText: {
                            label: 'Help Text',
                            type: 'string',
                            id: 'helpText',
                            helpText: 'Text to display when the (i) icon is clicked.'
                        },
                    },
                    newItem: {}
                }
            }
        },
        default: {
            primaryKey: 'someKey',
            itemType: 'string',
            permissions: {
                canMove: true,
                canDelete: true,
                allowAddItem: true
            },
            itemKeys: []
        }
    }
};
const controlEditorSettings = {
    controls: {
        label: 'Component controls',
        type: 'ARRAY',
        id: 'controls',
        itemConfig: {
            primaryKey: 'name',
            itemType: 'object',
            permissions: {
                allowAddItem: true,
                canDelete: true,
                canMove: true
            },
            keys: controlGlobalEditorSettings,
            newItem: {}
        }
    },
};

let setEditorSettingControlLogic = (_iComp, newSettings, type) => {
    for (let j in newSettings) {
        if (j === 'length') continue;
        if (!controlEditorSettings.controls.itemConfig.logic) controlEditorSettings.controls.itemConfig.logic = {};
        if (typeof controlEditorSettings.controls.itemConfig.logic[j] === 'undefined') {
            controlEditorSettings.controls.itemConfig.logic[j] = " this.type == '" + type + "' ";
        } else {
            controlEditorSettings.controls.itemConfig.logic[j] += " || this.type == '" + type + "' ";
        }
    }
};
for (let i in controlTypes) {
    if (i === 'length') continue;
    let cmp = controlTypes[i];
    if (cmp && cmp.editorSettings) {
        controlEditorSettings.controls.itemConfig.keys = { ...controlEditorSettings.controls.itemConfig.keys, ...cmp.editorSettings };
        setEditorSettingControlLogic(cmp, cmp.editorSettings, i);
    }
    if (cmp && (i === 'dropdown' || i === 'dropdowntree' || i === 'multiselect' || i === 'radio_button_list' || i === 'radio' || i === 'checkbox_list' || i === 'dropdownbutton')) {
        controlEditorSettings.controls.itemConfig.keys = {
            ...controlEditorSettings.controls.itemConfig.keys,
            ...optionControlEditorSettings,
            ...(i === 'dropdown' || i === 'dropdowntree' || i === 'multiselect' || i === 'dropdownbutton') ? dropdownControlEditorSettings : {}
        };
        setEditorSettingControlLogic(cmp, optionControlEditorSettings, i);
    }
    if (cmp && i === 'array') {
        controlEditorSettings.controls.itemConfig.keys = {
            ...controlEditorSettings.controls.itemConfig.keys,
            ...arrayControlEditorSettings,
        };
        setEditorSettingControlLogic(cmp, arrayControlEditorSettings, i);
    }
    if (cmp && i === 'text') {
        controlEditorSettings.controls.itemConfig.keys = {
            ...controlEditorSettings.controls.itemConfig.keys,
            ...textControlEditorSettings
        };
        setEditorSettingControlLogic(cmp, textControlEditorSettings, i);
    }
}
for (let i in controlEditorSettings.controls.itemConfig.keys) {
    if (i === 'length') continue;
    let defaultVal = controlEditorSettings.controls.itemConfig.keys[i].default || '';
    controlEditorSettings.controls.itemConfig.newItem[i] = defaultVal;
}
controlEditorSettings.controls.itemConfig.keys = { ...controlGlobalEditorSettings, ...controlEditorSettings.controls.itemConfig.keys };

const createStoreIndex = (storePath, name) => {
    let indexArray;
    if (Array.isArray(storePath)) indexArray = [...storePath, name];
    else if (storePath) indexArray = [storePath, name];
    else indexArray = ['global', name];
    let output = indexArray[0];
    for (let i = 1; i < indexArray.length; i++) {
        if (typeof indexArray[i] !== 'undefined') output += '/' + indexArray[i];
    }
    return output;
};

const formatControlLogic = (config, _this) => {
    let output;
    if (!config.logic) return false;
    if (typeof config.logic === 'string') {
        output = library.getRealIndexes(config.logic, _this, config.itemIndexArray);
    } else {
        output = library.cloneObj(config.logic);
        for (let cIdx = 0; cIdx < output.length; cIdx++) {
            if (output[cIdx].valueType === 'path') {
                output[cIdx].valuePath = library.getRealIndexes(output[cIdx].valuePath, _this, config.itemIndexArray);
            }
            if (typeof output[cIdx].criteria === 'string') {
                output[cIdx].criteria = library.getRealIndexes(output[cIdx].criteria, _this, config.itemIndexArray);
            }
            if (typeof output[cIdx].prop === 'string') {
                output[cIdx].prop = library.getRealIndexes(output[cIdx].prop, _this, config.itemIndexArray);
            }
        }
    }
    return output;
};

const controlStore = defineStore('controlStore', {
    state: () => ({
        // control configs will go here
    }),
    getters: {
        getValue: (state) => (controlName, storeId) => (state[createStoreIndex(storeId, controlName)].value)
    },
    actions: {
        setValue(controlName, storeId, value, parentStoreId) {
            let control = this[createStoreIndex(storeId, controlName)];
            if (control) {
                if (control.storePropValueSync && parentStoreId) {
                    store.state[parentStoreId][control.storePropValueSync] = value;
                }
                control.value = value;
                control.valid = true;
                control.validationErrors = [];
            }
        },
        addControl(config, _this, controlStoreIndex) {
            config = library.cloneObj(config);
            let name = config.name;
            let setEventFunctions = (controlConfig, eventName) => {
                if (controlConfig[eventName]) {
                    if (!controlConfig.eventMethods) controlConfig.eventMethods = {};
                    controlConfig.eventMethods[eventName] = controlConfig[eventName];
                    delete controlConfig[eventName];
                }
            };
            let storeIds = controlStoreIndex.split('/');
            let cId = storeIds[0].indexOf('layoutContainer') === 0 ? storeIds[1] : storeIds[0]; // component storeId
            if (_this && !_this.controls) _this.controls = {};
            if (typeof config.type === 'undefined') return;
            config.type = config.type.toLowerCase();
            let type = config.type;
            type = type.toLowerCase();
            if (!config.sorted && config.itemsSort && Array.isArray(config.items) && config.itemsSort.sortBy) {
                config.sorted = true;
                config.items = library.sortObject(config.items, config.itemsSort.sortBy, config.itemsSort.direction);
            }
            if ((config.type === 'dropdown' || config.type === 'dropdowntree' || config.type === 'multiselect') && typeof config.fields !== 'object') {
                config.fields = { text: 'text', value: 'value' };
            }
            if (!Array.isArray(config.items)) {
                // TODO Make sure this is helpful
                config.items = [];
            }
            // if (config.value && type === 'dropdown') {
            //     setDropdownProps(config);
            // }
            config.id = name + '_' + cId;
            config.realId = name;
            // Set some defaults
            if (typeof config.default !== 'undefined'
                && config.value === undefined
                && (!_this || (_this && typeof _this?.controls?.[name] === 'undefined'))
                && config.value !== config.default) config.value = config.default;
            if (typeof config.value === 'undefined') config.value = "";
            if ((config.type === 'multiselect' && config.type === 'dropdowntree') && !Array.isArray(config.value)) config.value = [];
            if (typeof config.initialValue === 'undefined') config.initialValue = library.cloneObj(config.value);
            if (typeof config.label === 'undefined') config.label = "";
            if (typeof config.cssClass !== 'string') config.cssClass = '';
            if (typeof config.display === 'undefined') config.display = true;
            if (typeof config.disabled === 'undefined' || config.disabled === '') config.disabled = false;
            if (typeof config.required === 'undefined' || config.required === '') config.required = false;
            if (typeof config.valid === 'undefined' && config.type === 'hidden') config.valid = true;
            if (typeof config.ENABLE_HTML !== 'undefined' && typeof config.enableHtml === 'undefined') config.enableHtml = config.ENABLE_HTML;
            // Set some control specific adjustments
            if (type === 'password') config.type = 'password';
            else if (type === 'checkbox' || type === 'boolean') {
                // config.type = 'boolean';
                config.value = !!(parseInt(config.value, 10) || config.value === true);
            }
            else if (type === 'checkbox_list') {
                if (typeof config.value !== 'object' || Array.isArray(config.value)) config.value = {};
            }
            else if (type === 'number' && !config['decimals']) {
                config['decimals'] = "0";
            }
            else if (type === 'string') {
                if (typeof config.value !== 'string') config.value = '';
            }
            else if (type === 'date' && !config.value) {
                config.value = null;
            } else if (type === 'actionfield' && typeof config.value === 'object' && !Array.isArray(config.value)) {
                config.value = [config.value];
            }
            // Set some default classes
            if (config.cssClass.indexOf('e-block') === -1) config.cssClass += ' e-block';
            if (_this && _this.component && _this.component.cType === 'settingsEditor') {
                let exclusions = ['button', 'boolean'];
                if (exclusions.indexOf(config.type) === -1) {
                    config.cssClass += ' e-outline';
                }
            }
            config.cssClass = config.cssClass.trim();
            setEventFunctions(config, 'onselect');
            setEventFunctions(config, 'onclick');
            setEventFunctions(config, 'onchange');
            setEventFunctions(config, 'oninput');
            setEventFunctions(config, 'ondblclick');
            setEventFunctions(config, 'onfiltering');
            setEventFunctions(config, 'onfocus');
            setEventFunctions(config, 'ondragstart');
            setEventFunctions(config, 'onchangeorder');
            setEventFunctions(config, 'oncomplete');
            setEventFunctions(config, 'onopen');
            if (typeof config.valid === 'undefined') config.valid = true;
            if (typeof config.validationErrors === 'undefined') config.validationErrors = [];
            if (typeof config.rules === 'undefined') config.rules = [];
            // add path to config
            config.controlStoreIndex = controlStoreIndex;
            // add to store with path
            if (this[controlStoreIndex]) delete this[controlStoreIndex];
            this[controlStoreIndex] = config;
            // link to component's this.controls
            if (_this) _this.controls[name] = this[controlStoreIndex];
        },
        removeControl(_this) {
            // TODO should this do something more?
            if (_this.controlStoreIndex) {
                delete this[_this.controlStoreIndex];
            } else if (_this.componentStoreIndex) {
                // delete all controls for component
                for (let i in this) {
                    if (typeof i === 'string' && i.startsWith(_this.componentStoreIndex + '/')) {
                        delete this[i];
                    }
                }
            }
        }
    }
});

export {
    controlStore, controlTypes, controlEditorSettings, createStoreIndex, formatControlLogic
};
